// Visual Haircut History & Style Library Types

// Enums
export enum PhotoType {
  Before = 'Before',
  After = 'After',
  Progress = 'Progress'
}

// Photo Types
export interface CutHistoryPhotoDto {
  id: number;
  cutHistoryId: number;
  photoType: PhotoType;
  photoUrl: string;
  thumbnailUrl: string;
  compressedUrl: string;
  width: number;
  height: number;
  photoTakenAt?: string;
  cameraModel?: string;
  qualityScore: number;
  aiGeneratedTags?: string[];
  aiStyleAnalysis?: string;
  likeCount: number;
  shareCount: number;
  uploadedAt: string;
  uploadedBy: string;
}

// Cut History Types
export interface CutHistoryDto {
  id: number;
  bookingId: number;
  userId: string;
  barberId: number;
  barberName: string;
  barbershopId: number;
  barbershopName: string;
  serviceDate: string;
  serviceName: string;
  cutStyle: string;
  fadeType?: string;
  lengthOnTop?: string;
  lengthOnSides?: string;
  serviceNotes?: string;
  barberNotes?: string;
  clientSatisfactionRating?: number;
  hasBeforePhoto: boolean;
  hasAfterPhoto: boolean;
  totalPhotoCount: number;
  styleTags: string[];
  primaryStyle: string;
  isPublicInGallery: boolean;
  showInBarberPortfolio: boolean;
  publicViewCount: number;
  likeCount: number;
  hasBeenRebooked: boolean;
  timesRebooked: number;
  lastRebookedAt?: string;
  createdAt: string;
  beforePhotoUrl?: string;
  afterPhotoUrl?: string;
}

export interface CutHistoryDetailDto extends CutHistoryDto {
  photos: CutHistoryPhotoDto[];
  productsUsed: string[];
  durationMinutes: number;
  priceCharged: number;
  tipAmount?: number;
  totalAmount: number;
  weatherCondition?: string;
  specialOccasion?: string;
}

// Visual Timeline Types
export interface TimelineFilters {
  barberId?: number;
  barbershopId?: number;
  styleTag?: string;
  startDate?: string;
  endDate?: string;
  hasPhotos?: boolean;
  minRating?: number;
  page?: number;
  pageSize?: number;
}

export interface VisualTimelineDto {
  items: CutHistoryDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  statistics: TimelineStatistics;
}

export interface TimelineStatistics {
  totalCuts: number;
  totalPhotos: number;
  uniqueStyles: number;
  favoriteBarber?: string;
  mostUsedStyle?: string;
  averageRating: number;
  totalSpent: number;
}

// Shared Basic Info Types (match backend BarberBasicInfoDto / BarbershopBasicInfoDto)
export interface BarberBasicInfoDto {
  id: number;
  name: string;
  bio?: string;
  photoUrl?: string;
  averageRating?: number;
}

export interface BarbershopBasicInfoDto {
  id: number;
  name: string;
  address: string;
  city?: string;
  state?: string;
  phone?: string;
}

// Photo metadata (matches backend PhotoMetadataDto)
export interface PhotoMetadataDto {
  id: number;
  fileUrl: string;
  thumbnailUrl?: string;
  photoType: string;
  width?: number;
  height?: number;
  likeCount: number;
  isFeatured: boolean;
}

// Style Library Types (match backend StyleLibraryItemDto)
export interface StyleLibraryItemDto {
  id: number;
  cutHistoryId: number;
  styleName: string;
  styleDescription?: string;
  styleTags: string[];

  // Photos
  primaryPhotoUrl?: string;
  photos: PhotoMetadataDto[];

  // Booking Stats
  timesBooked: number;
  lastBookedAt?: string;
  isBookable: boolean;

  // Preferences
  isFavorite: boolean;
  preferredBarber?: BarberBasicInfoDto;
  specialInstructions?: string;

  // Original Cut Details
  originalServiceDate: string;
  barbershop: BarbershopBasicInfoDto;

  createdAt: string;
}

export interface CreateStyleLibraryItemDto {
  cutHistoryId: number;
  styleName: string;
  styleDescription?: string;
  styleTags?: string[];
  primaryPhotoId?: number;
  photoIds?: number[];
  isFavorite?: boolean;
  preferredBarberId?: number;
  specialInstructions?: string;
}

export interface UpdateStyleLibraryItemDto {
  styleName?: string;
  description?: string;
  isFavorite?: boolean;
  season?: string;
  occasions?: string[];
  enableMaintenanceReminder?: boolean;
  maintenanceIntervalDays?: number;
}

export interface BookStyleAgainDto {
  styleLibraryItemId: number;
  preferredDateTime: string;
  barberIdOverride?: number;
  additionalNotes?: string;
}

// Gallery Types (match backend StyleGalleryItemDto)
export interface StyleGalleryItemDto {
  id: number;
  cutHistoryId: number;
  title: string;
  description?: string;
  styleTags: string[];

  // Primary Photo
  primaryPhotoUrl: string;
  thumbnailUrl?: string;

  // Barber & Shop Info
  barber: BarberBasicInfoDto;
  barbershop: BarbershopBasicInfoDto;

  // Engagement
  viewCount: number;
  likeCount: number;
  bookmarkCount: number;
  timesBooked: number;

  // Status
  isFeatured: boolean;
  isLikedByCurrentUser: boolean;
  isSavedByCurrentUser: boolean;

  publishedAt: string;
}

export interface CreateGalleryItemDto {
  cutHistoryId: number;
  photoId?: number;
  title: string;
  description: string;
  styleTags?: string[];
}

export interface GalleryFilters {
  styleTags?: string[];
  barberId?: number;
  barbershopId?: number;
  isFeatured?: boolean;
  searchTerm?: string;
  sortBy?: 'Popular' | 'Recent' | 'MostLiked' | 'MostBooked';
  page?: number;
  pageSize?: number;
}

export interface SubmitToGalleryDto {
  cutHistoryId: number;
  photoId?: number;
  title: string;
  description: string;
  styleTags?: string[];
}

// Barber Portfolio Types (match backend BarberPortfolioDto)
export interface BarberPortfolioDto {
  barber: BarberBasicInfoDto;
  featuredWork: StyleGalleryItemDto[];
  stats: PortfolioStatsDto;
}

export interface PortfolioStatsDto {
  totalPublicStyles: number;
  totalLikes: number;
  totalViews: number;
  totalBookings: number;
  topStyles: string[];
  averageRating: number;
}

// Analytics Types (match backend VisualHistoryAnalyticsDto)
export interface VisualHistoryAnalyticsDto {
  // Overall Stats
  totalCutsWithPhotos: number;
  totalPhotos: number;
  totalStylesInLibrary: number;
  totalPublicInGallery: number;

  // Style Breakdown
  styleBreakdown: StyleBreakdownDto[];
  mostUsedTags: string[];

  // Barber Loyalty
  barberLoyalty: BarberLoyaltyDto[];

  // Rebooking Patterns
  averageTimeBetweenCuts: number; // In days
  totalRebookings: number;
  mostRebookedStyles: string[];

  // Engagement
  totalLikesReceived: number;
  totalViewsReceived: number;
}

export interface StyleBreakdownDto {
  styleName: string;
  count: number;
  percentage: number;
}

export interface BarberLoyaltyDto {
  barberId: number;
  barberName: string;
  visitCount: number;
  lastVisit?: string;
}

// Photo Upload Types
export interface PhotoUploadDto {
  file: File;
  photoType: PhotoType;
}

export interface PhotoUploadProgress {
  fileName: string;
  progress: number;
  status: 'uploading' | 'processing' | 'completed' | 'error';
  error?: string;
  photoUrl?: string;
}

// Search & Discovery Types
export interface SearchGalleryDto {
  query: string;
  filters?: GalleryFilters;
}

// Matches backend TrendingStyleDto
export interface TrendingStyle {
  styleName: string;
  tags: string[];
  timesBookedThisMonth: number;
  totalLikes: number;
  totalViews: number;
  trendScore: number;
  exampleStyles: StyleGalleryItemDto[];
}

export interface StyleSuggestion {
  styleName: string;
  description: string;
  suitableFor: string[];
  examplePhotos: string[];
  popularityScore: number;
}

// Request/Response Types
export interface PaginatedGalleryResponse {
  items: StyleGalleryItemDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface UploadPhotoResponse {
  photo: CutHistoryPhotoDto;
  cutHistory: CutHistoryDto;
  message: string;
}

export interface VisualHistoryBookingResponse {
  id: number;
  bookingDateTime: string;
  status: string;
  message: string;
}

// View Models for UI Components
export interface VisualTimelineItemViewModel extends CutHistoryDto {
  displayDate: string;
  timeAgo: string;
  canUploadPhotos: boolean;
  canAddToLibrary: boolean;
  canShareToGallery: boolean;
}

export interface StyleLibraryItemViewModel extends StyleLibraryItemDto {
  displayLastUsed: string;
  isOverdueForMaintenance: boolean;
  daysUntilMaintenance?: number;
  quickBookAvailable: boolean;
}

// Error Types
export interface VisualHistoryError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

// Filter Options
export interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

export interface StyleTagOption extends FilterOption {
  color?: string;
  icon?: string;
}

// Component Props Types
export interface VisualTimelineProps {
  filters?: TimelineFilters;
  onCutHistoryClick?: (cutHistoryId: number) => void;
  onBookAgain?: (cutHistoryId: number) => void;
}

export interface CutHistoryDetailProps {
  cutHistoryId: number;
  onClose?: () => void;
  onAddToLibrary?: () => void;
  onShareToGallery?: () => void;
}

export interface StyleLibraryProps {
  onBookStyle?: (styleId: number) => void;
  onEditStyle?: (styleId: number) => void;
  onDeleteStyle?: (styleId: number) => void;
}

export interface PhotoUploadWidgetProps {
  bookingId: number;
  existingPhotos?: CutHistoryPhotoDto[];
  onUploadComplete?: (photos: CutHistoryPhotoDto[]) => void;
  maxPhotos?: number;
  allowedPhotoTypes?: PhotoType[];
}

export interface BeforeAfterComparisonProps {
  beforePhotoUrl: string;
  afterPhotoUrl: string;
  cutHistory?: CutHistoryDto;
  viewMode?: 'side-by-side' | 'slider' | 'fade';
}

export interface PublicGalleryProps {
  initialFilters?: GalleryFilters;
  onItemClick?: (item: StyleGalleryItemDto) => void;
  onBookStyle?: (item: StyleGalleryItemDto) => void;
}

export interface BarberPortfolioProps {
  barberId: number;
  onBookWithBarber?: (barberId: number) => void;
}

// State Management Types
export interface VisualHistoryState {
  timeline: VisualTimelineDto | null;
  currentDetail: CutHistoryDetailDto | null;
  styleLibrary: StyleLibraryItemDto[];
  gallery: PaginatedGalleryResponse | null;
  analytics: VisualHistoryAnalyticsDto | null;
  isLoading: boolean;
  error: VisualHistoryError | null;
  uploadProgress: PhotoUploadProgress[];
}

export interface VisualHistoryActions {
  loadTimeline: (filters?: TimelineFilters) => Promise<void>;
  loadCutHistoryDetail: (id: number) => Promise<void>;
  uploadPhoto: (bookingId: number, file: File, photoType: PhotoType) => Promise<void>;
  loadStyleLibrary: () => Promise<void>;
  addToStyleLibrary: (data: CreateStyleLibraryItemDto) => Promise<void>;
  updateStyleLibraryItem: (id: number, data: UpdateStyleLibraryItemDto) => Promise<void>;
  deleteStyleLibraryItem: (id: number) => Promise<void>;
  bookStyleAgain: (styleId: number, data: BookStyleAgainDto) => Promise<void>;
  loadGallery: (filters?: GalleryFilters) => Promise<void>;
  submitToGallery: (data: SubmitToGalleryDto) => Promise<void>;
  likeGalleryItem: (id: number) => Promise<void>;
  loadAnalytics: () => Promise<void>;
}
